import { CheckCircleIcon, ExclamationCircleIcon, XMarkIcon } from '@heroicons/react/24/solid';
import { useState } from 'react';
import toast from 'react-hot-toast';
import { ToastNotificationType } from 'shared/enums';
import { cn } from 'shared/utils';

/**
 * Props for the AlertMessage component.
 */
interface AlertMessageProps {
  /** The message to display in the alert. */
  message: string;
  /** The type of toast notification. */
  type: ToastNotificationType;
  /** The ID of the toast notification. */
  toastId?: string;
  /** Flag to show or hide the close icon. */
  showCloseIcon?: boolean;
  /** Additional CSS classes for styling. */
  className?: string;
}

/**
 * Standard alert messages to display throughout the application.
 * Show/Hide can be controlled by toast or by local state.
 * If toastId is passed then we are controlling with the toast library.
 * Else, we are controlling with local state.
 *
 * @param param - props for configuring theming and functionality of alert message
 * @returns React Functional Component
 */
export const AlertMessage: React.FC<AlertMessageProps> = ({ message, type, showCloseIcon, className, toastId }) => {
  const [showNotification, setShowNotification] = useState<boolean>(true);

  const LeftIcon = type !== ToastNotificationType.Success ? ExclamationCircleIcon : CheckCircleIcon;

  /**
   * Handle removing the alert from the DOM.
   * If there is a toast, let toast library handle removing.
   * Else, let local state handle it.
   */
  const onClose = () => {
    if (toastId) {
      toast.remove(toastId);
    } else {
      setShowNotification(false);
    }
  };
  const colorMap = {
    [ToastNotificationType.Success]: {
      bg: 'bg-green-50',
      border: 'border-green-400',
      icon: 'text-green-400',
      text: 'text-green-800',
      close: 'text-green-500',
    },
    [ToastNotificationType.Error]: {
      bg: 'bg-red-50',
      border: 'border-red-400',
      icon: 'text-red-400',
      text: 'text-red-800',
      close: 'text-red-500',
    },
    [ToastNotificationType.Warning]: {
      bg: 'bg-yellow-50',
      border: 'border-yellow-400',
      icon: 'text-yellow-400',
      text: 'text-yellow-800',
      close: 'text-yellow-500',
    },
    [ToastNotificationType.Info]: {
      bg: 'bg-blue-50',
      border: 'border-blue-400',
      icon: 'text-blue-400',
      text: 'text-blue-800',
      close: 'text-blue-500',
    },
  };
  const styles = colorMap[type];
  if (!toastId && !showNotification) return null;

  return (
    <div
      className={cn(
        'flex items-center w-1/2 py-4 px-5 rounded-md text-sm font-medium gap-4 shadow-sm border-l-4',
        styles.bg,
        styles.border,
        className
      )}
    >
      <div className="rounded-full">
        <LeftIcon className={cn('h-5', styles.icon)} />
      </div>
      <div className={cn('flex-grow', styles.text)}>{message}</div>
      {showCloseIcon && (
        <div>
          <XMarkIcon className={cn('h-4 cursor-pointer', styles.close)} onClick={onClose} />
        </div>
      )}
    </div>
  );
};
